import { AnyAction } from "redux-saga";
import {
  all,
  call,
  debounce,
  put,
  takeLatest,
  takeLeading,
  delay,
  takeEvery,
  select,
  fork,
} from "redux-saga/effects";

import { ApiService, Case, CasePATCH, CaseReport, Clinic } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { SysState } from "reducers/sysReducer";

import {
  fetchCopathPatientMatches,
  fetchPatientMatches,
} from "../actions/caseDetailActions";
import {
  CASE_DETAIL_REQUEST,
  CASE_DETAIL_FAILURE,
  CASE_DETAIL_SUCCESS,
  CASE_OPEN_DOCUMENT_REQUEST,
  CASE_OPEN_DOCUMENT_FAILURE,
  CASE_OPEN_DOCUMENT_SUCCESS,
  CASE_OPEN_DOCUMENT_MISSING,
  DIAGNOSE_REQUEST,
  DIAGNOSE_SUCCESS,
  DIAGNOSE_FAILURE,
  CASE_SIGN_OUT_REQUEST,
  CASE_SIGN_OUT_SUCCESS,
  CASE_SIGN_OUT_FAILURE,
  UPDATE_DIAGNOSIS_FAILURE,
  UPDATE_DIAGNOSIS_SUCCESS,
  CASE_DIAGNOSES_REQUEST,
  CASE_DIAGNOSES_SUCCESS,
  CASE_DIAGNOSES_FAILURE,
  OPTIMISTICALLY_EDIT_DIAGNOSIS,
  CASE_PATCH_STATUS_SUCCESS,
  CASE_PATCH_REQUEST,
  CASE_PATCH_SUCCESS,
  CASE_PATCH_FAILURE,
  CASE_PATCH_STATUS_REQUEST,
  CASE_PATCH_STATUS_FAILURE,
  CASE_DETAIL_EVENTS_FAILURE,
  CASE_DETAIL_EVENTS_SUCCESS,
  CASE_DETAIL_EVENTS_REQUEST,
  UPDATE_CASE_ADDON_REQUEST,
  UPDATE_CASE_ADDON_SUCCESS,
  UPDATE_CASE_ADDON_FAILURE,
  CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS,
  CANCEL_EDIT_SCREEN_DIAGNOSES_FAILURE,
  CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST,
  EDIT_SCREEN_DIAGNOSES_FAILURE,
  EDIT_SCREEN_DIAGNOSES_SUCCESS,
  EDIT_SCREEN_DIAGNOSES_REQUEST,
  CASE_RECALL_REPORT_SUCCESS,
  CASE_RECALL_REPORT_FAILURE,
  CASE_RECALL_REPORT_REQUEST,
  CASE_PATIENT_MATCHES_FAILURE,
  MODIFY_REPORT_SUCCESS,
  MODIFY_REPORT_FAILURE,
  CANCEL_MODIFY_REPORT_FAILURE,
  CANCEL_MODIFY_REPORT_SUCCESS,
  MODIFY_REPORT_REQUEST,
  CANCEL_MODIFY_REPORT_REQUEST,
  COPATH_CASE_PATIENT_MATCHES_FAILURE,
} from "../actions/constants";
import { addToast, nextId, removeToast } from "../actions/toastActions";
import { downloadDocument } from "../utils/documentUtils";

function* requestCaseDetail(action: any): any {
  try {
    const payload: Case = yield call(ApiService.retrieveCase, action.payload);
    yield put({ type: CASE_DETAIL_SUCCESS, payload });
  } catch (e) {
    yield put({ type: CASE_DETAIL_FAILURE, error: true, payload: e });
  }
}

function* requestPatientMatches(action: any): any {
  try {
    const casePayload: Case = action.payload;
    const features = yield select(
      ({ sys }: { sys: SysState }) => sys?.features
    );

    if (
      features?.patientHistory &&
      casePayload?.id &&
      !casePayload?.hasOwnProperty("patient_matches")
    ) {
      yield put(yield call(fetchPatientMatches, { case_id: casePayload.id }));
    }
  } catch (e) {
    yield put({ type: CASE_PATIENT_MATCHES_FAILURE, error: true, payload: e });
  }
}

function* requestCopathPatientMatches(action: AnyAction): any {
  try {
    const casePayload: Case = action.payload;

    if (
      casePayload?.id &&
      casePayload?.patient?.clinic?.client_transition_type &&
      casePayload?.patient?.clinic?.sonic_practice?.acronym === "PRO" &&
      casePayload?.patient?.clinic?.client_type ===
        Clinic.client_type.CONVERSION
    ) {
      yield put(
        yield call(fetchCopathPatientMatches, {
          case_id: casePayload.id,
          patient: casePayload.patient,
        })
      );
    }
  } catch (e) {
    yield put({
      type: COPATH_CASE_PATIENT_MATCHES_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* requestCaseLog(action: any): any {
  try {
    const caseLog = yield call(ApiService.listCaseEventLogs, action.payload);
    yield put({ type: CASE_DETAIL_EVENTS_SUCCESS, payload: caseLog });
  } catch (e) {
    yield put({ type: CASE_DETAIL_EVENTS_FAILURE, error: true, payload: e });
  }
}

function* openDocument(action: any): any {
  try {
    //@ts-ignore -- TODO: ApiService seems to have lost `source`
    const result = yield call(ApiService.retrieveCaseDocument, {
      id: action.payload.id,
      parentLookupCaseId: action.payload.parentLookupCaseId,
      source: action.payload.source,
    });
    if (!result.presigned_url) {
      yield all([
        put(
          addToast(
            {
              kind: "danger",
              title: "Document Missing",
              message:
                "If this problem persists, please contact support@pathologywatch.com",
            },
            nextId()
          )
        ),
        put({ type: CASE_OPEN_DOCUMENT_MISSING, payload: action.payload.id }),
      ]);
    } else {
      if (action.payload.downloadFileName) {
        yield call(
          downloadDocument,
          result.presigned_url,
          action.payload.downloadFileName
        );
      } else {
        window.open(result.presigned_url, "_blank");
      }
      yield put({
        type: CASE_OPEN_DOCUMENT_SUCCESS,
        payload: {
          ...result,
          case_id: action.payload.parentLookupCaseId,
          source: action.payload.source,
          user: action.payload.user,
        },
      });
    }
  } catch (e) {
    yield put({
      type: CASE_OPEN_DOCUMENT_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* fetchCaseDiagnoses(action: any): any {
  try {
    const diagnoses = yield call(ApiService.diagnosesCase, {
      id: action.payload.id,
    });
    yield put({
      type: CASE_DIAGNOSES_SUCCESS,
      payload: {
        diagnoses,
        status: action.payload.status,
        ai_only: action.payload.ai_only,
      },
    });
  } catch (e) {
    yield put({ type: CASE_DIAGNOSES_FAILURE, error: true, payload: e });
  }
}

function* createCaseDiagnosis(action: any): any {
  try {
    const caseDiagnosis = yield call(ApiService.createCaseDiagnosis, {
      requestBody: action.payload.caseDiagnosis,
    });
    yield put({
      type: DIAGNOSE_SUCCESS,
      payload: { caseDiagnosis },
    });
  } catch (e) {
    yield put({ type: DIAGNOSE_FAILURE, error: true, payload: e });
  }
}

function* updateCaseDiagnosis(action: any): any {
  try {
    const fn = ApiService.updateCaseDiagnosis;
    const caseDiagnosis = yield call(fn, {
      id: action.payload.diagnosis.id,
      requestBody: action.payload.diagnosis,
    });
    yield put({
      type: UPDATE_DIAGNOSIS_SUCCESS,
      payload: { caseDiagnosis },
    });
  } catch (e: unknown) {
    if ((e as ApiError)?.body !== "Incorrect CPT, ZSD, or ICD10.")
      yield put({ type: UPDATE_DIAGNOSIS_FAILURE, error: true, payload: e });
  }
}

function* requestSignout(action: any): any {
  try {
    const { id, navigate, signature_id, report_type, location_code_id } =
      action.payload;

    if (report_type === CaseReport.report_type.TECHNICAL) {
      // TODO: this is the same ts-ignore from below, but it's not a complete
      // "Cases". We want to sign the body with the appropriate paramters
      //@ts-ignore not sure why this would want 'Cases' as a requestBody
      yield call(ApiService.completeTechnicalCase, {
        id: id,
        requestBody: {
          report_type: report_type,
        },
      });
    } else {
      //@ts-ignore not sure why this would want 'Cases' as a requestBody
      yield call(ApiService.completeCase, {
        id: id,
        requestBody: {
          signature: signature_id,
          report_type: report_type,
          location_code_id: location_code_id,
        },
      });
    }

    yield put({
      type: CASE_SIGN_OUT_SUCCESS,
      payload: {
        id,
        navigate,
      },
    });
  } catch (e) {
    yield put({ type: CASE_SIGN_OUT_FAILURE, error: true, payload: e });
  }
}

function* recallReport(action: any): any {
  try {
    const response = yield call(ApiService.undoCase, { id: action.payload });
    yield put({
      type: CASE_RECALL_REPORT_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: CASE_RECALL_REPORT_FAILURE, error: true, payload: e });
  }
}

function* patchCaseWorkflow(action: any): any {
  const { case_id: id, ...requestBody } = action.payload;
  try {
    const result = yield call(ApiService.partialUpdateCase, {
      id,
      requestBody,
    });
    yield put({ type: CASE_PATCH_SUCCESS, payload: result });
  } catch (e) {
    yield put({
      type: CASE_PATCH_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* patchCaseStatus(action: any): any {
  try {
    const result = yield call(ApiService.partialUpdateCase, {
      id: action.payload.id,
      requestBody: { status: action.payload.status } as CasePATCH,
    });
    yield put({ type: CASE_PATCH_STATUS_SUCCESS, payload: result });
  } catch (e) {
    yield put({ type: CASE_PATCH_STATUS_FAILURE, error: true, payload: e });
  }
}

function* patchCaseAddon(action: any): any {
  try {
    const result = yield call(ApiService.partialUpdateCaseDiagnosisAddon, {
      id: action.payload.id,
      requestBody: {
        type: action.payload.type,
        content: action.payload.content,
      } as any,
    });
    yield put({
      type: UPDATE_CASE_ADDON_SUCCESS,
      payload: result,
    });
  } catch (e) {
    yield put({
      type: UPDATE_CASE_ADDON_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* displayPatchCaseStatusSuccess(action: any): any {
  const newStatus = action.payload.status;
  const accession = action.payload.accession;
  const toastId = nextId();
  yield put(
    addToast(
      {
        kind: "success",
        title: `${accession} changed to status ${newStatus}.`,
      },
      toastId
    )
  );
  yield delay(3000);
  yield put(removeToast(toastId));
}

function* initiateModifyReportProcedure(action: any): any {
  try {
    //@ts-ignore
    const result = yield call(ApiService.modifyReportCase, {
      id: action.payload.case_id,
      requestBody: { type: action.payload.source },
    });
    yield put({ type: MODIFY_REPORT_SUCCESS, payload: result });
  } catch (e) {
    yield put({
      type: MODIFY_REPORT_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* cancelModifyReportProcedure(action: any): any {
  try {
    //@ts-ignore
    const result = yield call(ApiService.modifyReportCase1, {
      id: action.payload.case_id,
    });
    yield put({ type: CANCEL_MODIFY_REPORT_SUCCESS, payload: result });
  } catch (e) {
    yield put({
      type: CANCEL_MODIFY_REPORT_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* editScreenDiagnoses(action: any): any {
  try {
    //@ts-ignore
    const result = yield call(ApiService.editScreenCase, {
      id: action.payload.case_id,
      requestBody: {},
    });
    yield put({ type: EDIT_SCREEN_DIAGNOSES_SUCCESS, payload: result });
  } catch (e) {
    yield put({
      type: EDIT_SCREEN_DIAGNOSES_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* cancelEditScreenDiagnoses(action: any): any {
  try {
    //@ts-ignore
    const result = yield call(ApiService.editScreenCase1, {
      id: action.payload.case_id,
    });
    yield put({ type: CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS, payload: result });
  } catch (e) {
    yield put({
      type: CANCEL_EDIT_SCREEN_DIAGNOSES_FAILURE,
      error: true,
      payload: e,
    });
  }
}

function* startPatientMatching(action: AnyAction) {
  yield fork(requestPatientMatches, action);

  const casePayload: Case = action.payload;
  if (
    casePayload?.id &&
    casePayload?.patient?.clinic?.client_transition_type &&
    casePayload?.patient?.clinic?.sonic_practice?.acronym === "PRO" &&
    casePayload?.patient?.clinic?.client_type === Clinic.client_type.CONVERSION
  ) {
    yield fork(requestCopathPatientMatches, action);
  }
}

export function* caseDetailSaga() {
  yield takeLatest(CASE_DETAIL_REQUEST, requestCaseDetail);
  yield takeLatest(CASE_DETAIL_EVENTS_REQUEST, requestCaseLog);
  yield takeLatest(CASE_OPEN_DOCUMENT_REQUEST, openDocument);
  yield takeLeading(CASE_DIAGNOSES_REQUEST, fetchCaseDiagnoses);
  yield takeLatest(DIAGNOSE_REQUEST, createCaseDiagnosis);
  yield takeLeading(CASE_SIGN_OUT_REQUEST, requestSignout);
  yield takeLeading(CASE_RECALL_REPORT_REQUEST, recallReport);
  yield takeLatest(CASE_PATCH_REQUEST, patchCaseWorkflow);
  yield takeLatest(CASE_PATCH_STATUS_REQUEST, patchCaseStatus);
  yield takeEvery(CASE_PATCH_STATUS_SUCCESS, displayPatchCaseStatusSuccess);
  yield takeLatest(MODIFY_REPORT_REQUEST, initiateModifyReportProcedure);
  yield takeLatest(CANCEL_MODIFY_REPORT_REQUEST, cancelModifyReportProcedure);
  yield takeLatest(EDIT_SCREEN_DIAGNOSES_REQUEST, editScreenDiagnoses);
  yield takeLatest(
    CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST,
    cancelEditScreenDiagnoses
  );
  yield takeLatest(CASE_DETAIL_SUCCESS, startPatientMatching);
  yield debounce(500, UPDATE_CASE_ADDON_REQUEST, patchCaseAddon);

  yield debounce(500, OPTIMISTICALLY_EDIT_DIAGNOSIS, updateCaseDiagnosis);
}
